import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { shopifyApi } from '@/api/shopify.api';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Badge } from '@/components/ui/Badge';
import { Field } from '@/components/ui/Field';
import { Modal } from '@/components/ui/Modal';
import { useToasts } from '@/context/ToastContext';
import { useAuth } from '@/context/AuthContext';
import { ApiError } from '@/lib/errors';
import { ViewType } from '@/types/domain';
import { formatDateTime } from '@/lib/format';
import type {
  BulkStoreSecretEntry,
  RegisterWebhookPayload,
  ShopifyStoreAdmin,
  UpdateStorePayload
} from '@/types/api';

export function ShopifyPage() {
  const queryClient = useQueryClient();
  const { push } = useToasts();
  const { user, hasCapability } = useAuth();
  // V4.3 — capability-driven so admin can grant Shopify management to a
  // non-admin (e.g. a senior Sales Lead) via the Permissions matrix.
  const canManageShopify = hasCapability('shopify.manage');

  const storesQuery = useQuery({
    queryKey: ['shopify', 'stores'],
    queryFn: () => shopifyApi.listStores()
  });

  const summaryQuery = useQuery({
    queryKey: ['shopify', 'webhook-summary'],
    queryFn: () => shopifyApi.webhookSummary(),
    refetchInterval: 30_000
  });

  const recentWebhooksQuery = useQuery({
    queryKey: ['shopify', 'recent-webhooks'],
    queryFn: () => shopifyApi.listRecentWebhooks(50),
    refetchInterval: 15_000
  });

  const [selectedStoreCode, setSelectedStoreCode] = useState<string>('');
  const [orderNumber, setOrderNumber] = useState('');
  const [editTarget, setEditTarget] = useState<ShopifyStoreAdmin | null>(null);
  const [webhookStore, setWebhookStore] = useState<ShopifyStoreAdmin | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const syncMutation = useMutation({
    mutationFn: () => shopifyApi.syncOrder(selectedStoreCode, orderNumber),
    onSuccess: (res) => {
      push({
        kind: 'success',
        title: `Order ${res.order_number} linked`,
        message: `Opens in Shopify admin via secure deep link.`
      });
    },
    onError: (err: unknown) => {
      push({
        kind: 'error',
        title: 'Shopify sync failed',
        message: err instanceof ApiError ? err.problem?.detail ?? err.message : 'Unknown error'
      });
    }
  });

  React.useEffect(() => {
    const stores = storesQuery.data;
    if (stores && stores.length > 0 && !selectedStoreCode) {
      setSelectedStoreCode(stores[0].code);
    }
  }, [storesQuery.data, selectedStoreCode]);

  // V4.5c — surface OAuth redirect outcome from the URL once, then clean up.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauth = params.get('oauth');
    if (!oauth) return;
    const storeQ = params.get('store');
    if (oauth === 'success') {
      push({
        kind: 'success',
        title: `Shopify OAuth installed for ${storeQ ?? 'store'}`,
        message: 'Access token saved. Live order/draft lookup is now active.'
      });
      void queryClient.invalidateQueries({ queryKey: ['shopify', 'stores'] });
    } else if (oauth === 'failed') {
      push({
        kind: 'error',
        title: 'Shopify OAuth failed',
        message: params.get('error') ?? 'Unknown error during OAuth handshake'
      });
    }
    // strip the params so a refresh doesn't re-toast
    window.history.replaceState({}, '', '/shopify');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <div className="section-header">
        <h2>Shopify integrations</h2>
        <div className="flex gap-2">
          {canManageShopify ? (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setBulkOpen(true)}
            >
              Bulk set secrets
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={async () => {
              // V4.9 — invalidate + refetch + toast for visible feedback.
              try {
                await Promise.all([
                  queryClient.invalidateQueries({ queryKey: ['shopify', 'stores'] }),
                  queryClient.invalidateQueries({ queryKey: ['shopify', 'webhookSummary'] }),
                  queryClient.invalidateQueries({ queryKey: ['shopify', 'recentWebhooks'] })
                ]);
                await Promise.all([
                  storesQuery.refetch(),
                  summaryQuery.refetch(),
                  recentWebhooksQuery.refetch()
                ]);
                push({ kind: 'success', title: 'Shopify data refreshed' });
              } catch (err) {
                push({
                  kind: 'error',
                  title: 'Refresh failed',
                  message: err instanceof Error ? err.message : 'Network error'
                });
              }
            }}
            disabled={storesQuery.isFetching}
          >
            {storesQuery.isFetching ? 'Refreshing...' : 'Refresh all'}
          </button>
        </div>
      </div>

      <WebhookHealthStrip
        summary={summaryQuery.data ?? []}
        loading={summaryQuery.isLoading}
      />

      <div className="detail-grid">
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Connected stores</h3>
          </div>
          <div className="card-body no-pad">
            {storesQuery.isLoading ? (
              <LoadingState />
            ) : storesQuery.isError ? (
              <ErrorState error={storesQuery.error} onRetry={() => storesQuery.refetch()} />
            ) : (
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Name</th>
                      <th>Shopify domain</th>
                      <th>Currency</th>
                      <th>Geo</th>
                      <th>API version</th>
                      <th>Access token</th>
                      <th>Webhook secret</th>
                      {/* V4.9.5+ — surface OAuth credential status per geo so
                          admin can see at a glance which geos have Client
                          ID + Secret saved (was previously only visible by
                          opening each store's edit modal one-by-one). */}
                      <th>Client ID</th>
                      <th>Client Secret</th>
                      <th>Status</th>
                      {canManageShopify ? <th /> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {(storesQuery.data ?? []).map((store) => (
                      <tr key={store.code} className="no-hover">
                        <td className="font-mono">{store.code}</td>
                        <td>{store.name}</td>
                        <td className="text-small">{store.shopifyDomain}</td>
                        <td>{store.defaultCurrency ?? '—'}</td>
                        <td>{store.geo ?? '—'}</td>
                        <td className="font-mono text-small">{store.apiVersion}</td>
                        <td>
                          {store.hasAccessToken ? (
                            <Badge tone="success" title={store.accessTokenPreview ?? undefined}>
                              {store.accessTokenPreview ?? 'set'}
                            </Badge>
                          ) : (
                            <Badge tone="warning">Missing</Badge>
                          )}
                        </td>
                        <td>
                          {store.hasWebhookSecret ? (
                            <Badge
                              tone="success"
                              title={store.webhookSecretPreview ?? undefined}
                            >
                              {store.webhookSecretPreview ?? 'Set'}
                            </Badge>
                          ) : (
                            <Badge tone="neutral">Optional</Badge>
                          )}
                        </td>
                        <td>
                          {store.clientId ? (
                            <Badge tone="success" title={store.clientId}>
                              {`${store.clientId.slice(0, 4)}...${store.clientId.slice(-4)}`}
                            </Badge>
                          ) : (
                            <Badge tone="neutral">—</Badge>
                          )}
                        </td>
                        <td>
                          {store.hasClientSecret ? (
                            <Badge
                              tone="success"
                              title={store.clientSecretPreview ?? undefined}
                            >
                              {store.clientSecretPreview ?? 'Set'}
                            </Badge>
                          ) : (
                            <Badge tone="neutral">—</Badge>
                          )}
                        </td>
                        <td>
                          {store.active ? (
                            <Badge tone="success">Active</Badge>
                          ) : (
                            <Badge tone="neutral">Inactive</Badge>
                          )}
                        </td>
                        {canManageShopify ? (
                          <td>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => setEditTarget(store)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => setWebhookStore(store)}
                                disabled={!store.hasAccessToken}
                                title={
                                  store.hasAccessToken
                                    ? 'Manage webhooks for this store'
                                    : 'Set an access token first'
                                }
                              >
                                Webhooks
                              </button>
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Sync Shopify order</h3>
          </div>
          <div className="card-body">
            <p className="text-small text-muted">
              Get the Shopify admin URL for any order number. This builds the Shopify deep link
              using the backend&apos;s per-store URL template.
            </p>
            <Field label="Store" required>
              <select
                className="form-select"
                value={selectedStoreCode}
                onChange={(e) => setSelectedStoreCode(e.target.value)}
                disabled={storesQuery.isLoading}
              >
                {(storesQuery.data ?? []).map((store) => (
                  <option key={store.code} value={store.code}>
                    {store.code} · {store.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Order number" required>
              <input
                className="form-input"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder="#1234"
              />
            </Field>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!selectedStoreCode || !orderNumber || syncMutation.isPending}
              onClick={() => syncMutation.mutate()}
            >
              {syncMutation.isPending ? 'Syncing...' : 'Get Shopify link'}
            </button>
            {syncMutation.data ? (
              <div className="mt-3">
                <a
                  href={syncMutation.data.shopify_admin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-link"
                >
                  Open in Shopify admin →
                </a>
                <div className="text-small text-muted mt-2">
                  {syncMutation.data.shopify_admin_url}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="card mt-4">
        <div className="card-header">
          <h3 className="card-title">Recent webhook events</h3>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => recentWebhooksQuery.refetch()}
            disabled={recentWebhooksQuery.isFetching}
          >
            {recentWebhooksQuery.isFetching ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        <div className="card-body no-pad">
          {recentWebhooksQuery.isLoading ? (
            <LoadingState />
          ) : recentWebhooksQuery.isError ? (
            <ErrorState
              error={recentWebhooksQuery.error}
              onRetry={() => recentWebhooksQuery.refetch()}
            />
          ) : (recentWebhooksQuery.data ?? []).length === 0 ? (
            <div className="state">
              <h4>No webhooks received yet</h4>
              <p className="text-small">
                Once Shopify delivers an event to <code>/api/v1/shopify/webhooks</code>, it will
                appear here with the store domain and topic.
              </p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Shop</th>
                    <th>Topic</th>
                    <th>Order</th>
                    <th>Status</th>
                    <th>Webhook ID</th>
                  </tr>
                </thead>
                <tbody>
                  {(recentWebhooksQuery.data ?? []).map((row) => (
                    <tr key={row.id} className="no-hover">
                      <td className="text-small">{formatDateTime(row.created_at)}</td>
                      <td className="text-small">{row.shop_domain}</td>
                      <td className="font-mono text-small">{row.topic}</td>
                      <td className="font-mono text-small">
                        {row.order_number ?? row.order_id ?? '—'}
                      </td>
                      <td>
                        {row.status === 'received' ? (
                          <Badge tone="success">received</Badge>
                        ) : (
                          <Badge tone="warning">{row.status}</Badge>
                        )}
                      </td>
                      <td className="font-mono text-small">{row.webhook_id ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editTarget ? (
        <StoreEditModal
          store={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['shopify', 'stores'] });
            void queryClient.invalidateQueries({ queryKey: ['shopify', 'webhook-summary'] });
            setEditTarget(null);
          }}
        />
      ) : null}

      {webhookStore ? (
        <WebhooksModal store={webhookStore} onClose={() => setWebhookStore(null)} />
      ) : null}

      {bulkOpen ? (
        <BulkSecretsModal
          stores={storesQuery.data ?? []}
          onClose={() => setBulkOpen(false)}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['shopify', 'stores'] });
            void queryClient.invalidateQueries({ queryKey: ['shopify', 'webhook-summary'] });
            setBulkOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

interface WebhookHealthStripProps {
  summary: Array<{
    code: string;
    name: string;
    has_webhook_secret: boolean;
    has_access_token: boolean;
    total_webhooks: number;
    last_webhook_at: string | null;
    active: boolean;
  }>;
  loading: boolean;
}

function WebhookHealthStrip({ summary, loading }: WebhookHealthStripProps) {
  if (loading) {
    return (
      <div className="card mb-3">
        <div className="card-body">
          <LoadingState />
        </div>
      </div>
    );
  }
  const total = summary.length;
  const withSecret = summary.filter((s) => s.has_webhook_secret).length;
  const withToken = summary.filter((s) => s.has_access_token).length;
  const receiving = summary.filter((s) => s.total_webhooks > 0).length;
  return (
    <div className="card mb-3">
      <div className="card-body">
        <div className="flex gap-4 flex-wrap">
          <HealthTile label="Stores configured" value={`${total} / 10`} tone="info" />
          <HealthTile
            label="Webhook secret set"
            value={`${withSecret} / ${total}`}
            tone={withSecret === total ? 'success' : withSecret > 0 ? 'warning' : 'danger'}
          />
          <HealthTile
            label="Access token set"
            value={`${withToken} / ${total}`}
            tone={withToken > 0 ? 'info' : 'neutral'}
          />
          <HealthTile
            label="Receiving webhooks"
            value={`${receiving} / ${total}`}
            tone={receiving === total ? 'success' : receiving > 0 ? 'warning' : 'danger'}
          />
        </div>
        {summary.length > 0 ? (
          <div className="mt-3 flex gap-2 flex-wrap">
            {summary.map((s) => (
              <div
                key={s.code}
                className="text-small"
                style={{
                  padding: '4px 8px',
                  borderRadius: 6,
                  border: '1px solid var(--border)',
                  background:
                    s.has_webhook_secret && s.total_webhooks > 0
                      ? 'rgba(34,197,94,0.08)'
                      : s.has_webhook_secret
                        ? 'rgba(234,179,8,0.08)'
                        : 'rgba(239,68,68,0.06)'
                }}
                title={
                  s.last_webhook_at
                    ? `Last event: ${s.last_webhook_at}`
                    : 'No webhooks received yet'
                }
              >
                <strong>{s.code}</strong>{' '}
                {s.has_webhook_secret ? 'OK' : 'NO SECRET'}{' '}
                {s.total_webhooks > 0 ? `· ${s.total_webhooks}` : ''}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function HealthTile({
  label,
  value,
  tone
}: {
  label: string;
  value: string;
  tone: 'success' | 'warning' | 'danger' | 'info' | 'neutral';
}) {
  const colorMap: Record<string, string> = {
    success: 'var(--success)',
    warning: 'var(--warning)',
    danger: 'var(--danger)',
    info: 'var(--info)',
    neutral: 'var(--muted)'
  };
  return (
    <div style={{ minWidth: 140 }}>
      <div className="text-small text-muted">{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, color: colorMap[tone] }}>{value}</div>
    </div>
  );
}

interface StoreEditProps {
  store: ShopifyStoreAdmin;
  onClose: () => void;
  onSaved: () => void;
}

function StoreEditModal({ store, onClose, onSaved }: StoreEditProps) {
  const { push } = useToasts();
  const [name, setName] = useState(store.name);
  const [shopifyDomain, setShopifyDomain] = useState(store.shopifyDomain);
  const [template, setTemplate] = useState(store.shopifyAdminUrlTemplate);
  const [currency, setCurrency] = useState(store.defaultCurrency ?? '');
  const [geo, setGeo] = useState(store.geo ?? '');
  const [apiVersion, setApiVersion] = useState(store.apiVersion);
  const [active, setActive] = useState(store.active);
  const [accessToken, setAccessToken] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [notes, setNotes] = useState(store.notes ?? '');
  // V4.5c — OAuth alternative: paste Client ID + Secret here, then click
  // "Install via OAuth" to delegate the access-token issuance to Shopify.
  const [clientId, setClientId] = useState(store.clientId ?? '');
  const [clientSecret, setClientSecret] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      const payload: UpdateStorePayload = {
        name: name !== store.name ? name : undefined,
        shopify_domain: shopifyDomain !== store.shopifyDomain ? shopifyDomain : undefined,
        shopify_admin_url_template:
          template !== store.shopifyAdminUrlTemplate ? template : undefined,
        default_currency:
          currency && currency !== (store.defaultCurrency ?? '') ? currency : undefined,
        geo: geo !== (store.geo ?? '') ? geo : undefined,
        api_version: apiVersion !== store.apiVersion ? apiVersion : undefined,
        active: active !== store.active ? active : undefined,
        access_token: accessToken.trim() ? accessToken.trim() : undefined,
        webhook_secret: webhookSecret.trim() ? webhookSecret.trim() : undefined,
        notes: notes !== (store.notes ?? '') ? notes : undefined,
        client_id: clientId.trim() !== (store.clientId ?? '') ? clientId.trim() : undefined,
        client_secret: clientSecret.trim() ? clientSecret.trim() : undefined
      };
      return shopifyApi.updateStore(store.code, payload);
    },
    onSuccess: () => {
      push({ kind: 'success', title: `Store ${store.code} updated` });
      onSaved();
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.problem?.detail ?? err.message : 'Update failed');
    }
  });

  return (
    <Modal
      open={true}
      size="lg"
      title={`Edit store — ${store.code} (${store.name})`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Saving...' : 'Save changes'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Display name">
          <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Shopify domain">
          <input
            className="form-input"
            value={shopifyDomain}
            onChange={(e) => setShopifyDomain(e.target.value)}
            placeholder="yourstore.myshopify.com"
          />
        </Field>
        <Field label="Admin URL template" hint="Use %s as the placeholder for the order number.">
          <input
            className="form-input"
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
          />
        </Field>
        <Field label="Default currency">
          <input
            className="form-input"
            value={currency}
            onChange={(e) => setCurrency(e.target.value.toUpperCase())}
            maxLength={3}
          />
        </Field>
        <Field label="Geo">
          <input className="form-input" value={geo} onChange={(e) => setGeo(e.target.value)} />
        </Field>
        <Field label="API version" hint="Shopify REST/GraphQL version, e.g. 2024-01">
          <input
            className="form-input"
            value={apiVersion}
            onChange={(e) => setApiVersion(e.target.value)}
          />
        </Field>
      </div>
      <Field
        label="Shopify Admin API access token"
        hint={
          store.hasAccessToken
            ? `Currently: ${store.accessTokenPreview ?? '(set)'}. Leave blank to keep.`
            : 'Create a custom app in Shopify, grant read_orders + write_webhooks scopes, paste the shpat_ token here.'
        }
      >
        <input
          type="password"
          className="form-input"
          value={accessToken}
          onChange={(e) => setAccessToken(e.target.value)}
          autoComplete="new-password"
          placeholder={store.hasAccessToken ? 'current token set' : 'shpat_...'}
        />
      </Field>
      <Field
        label="Webhook HMAC secret"
        hint={
          store.hasWebhookSecret
            ? `Currently: ${store.webhookSecretPreview ?? '(set)'}. Leave blank to keep.`
            : 'Set this to the Shopify app secret to verify incoming webhook signatures.'
        }
      >
        <input
          type="password"
          className="form-input"
          value={webhookSecret}
          onChange={(e) => setWebhookSecret(e.target.value)}
          autoComplete="new-password"
          placeholder={store.hasWebhookSecret ? 'current secret set' : ''}
        />
      </Field>
      <Field label="Active">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          <span className="text-small">Receive traffic for this store</span>
        </label>
      </Field>
      <Field label="Notes">
        <textarea
          className="form-textarea"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Field>

      {/* V4.5c — OAuth install panel. Alternative to pasting the shpat_
          access token directly. Paste Client ID + Secret here, click
          "Install via OAuth", complete consent in Shopify, and the
          access token is auto-saved. */}
      <div
        style={{
          marginTop: 20,
          padding: 14,
          border: '1px dashed var(--border-strong)',
          borderRadius: 8,
          background: 'rgba(37,99,235,0.04)'
        }}
      >
        <h4 style={{ margin: '0 0 6px', fontSize: 14 }}>OAuth install (alternative)</h4>
        <p className="text-small text-muted" style={{ marginBottom: 12 }}>
          Use this if you only have the Custom App's <strong>Client ID</strong> and{' '}
          <strong>Client Secret</strong> (no <code>shpat_</code> token visible). Paste both,
          save the form, then click "Install via OAuth" — Shopify will redirect, ask you
          to authorize, and the access token is created automatically.
        </p>
        <Field
          label="Client ID"
          hint="From Shopify Admin → Custom App → API credentials → Credentials section"
        >
          <input
            className="form-input"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            placeholder="e.g. 0a5a3f5f49c340d6a0edf6ce416c5c7a"
          />
        </Field>
        <Field
          label="Client Secret"
          hint={
            store.hasClientSecret
              ? `Currently: ${store.clientSecretPreview ?? '(set)'}. Leave blank to keep.`
              : 'Same Credentials section in Shopify; click the eye icon to reveal.'
          }
        >
          <input
            type="password"
            className="form-input"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            autoComplete="new-password"
            placeholder={store.hasClientSecret ? 'current secret set' : ''}
          />
        </Field>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={!store.clientId || !store.hasClientSecret}
          title={
            !store.clientId || !store.hasClientSecret
              ? 'Save Client ID + Client Secret first, then click Install.'
              : `Open Shopify consent for ${store.shopifyDomain}`
          }
          onClick={async () => {
            try {
              const result = await shopifyApi.startOAuthInstall(store.code);
              window.open(result.authUrl, '_blank', 'noopener,noreferrer');
              push({
                kind: 'info',
                title: 'Opened Shopify install page',
                message: `Authorize the app, then come back. Your access token will be saved automatically.`
              });
            } catch (err) {
              push({
                kind: 'error',
                title: 'Failed to start OAuth',
                message:
                  err instanceof ApiError ? err.problem?.detail ?? err.message : 'Unknown error'
              });
            }
          }}
        >
          Install via OAuth →
        </button>
        {!store.clientId || !store.hasClientSecret ? (
          <div className="text-small text-muted" style={{ marginTop: 6 }}>
            Tip: paste Client ID + Client Secret above, click Save changes, then click this button.
          </div>
        ) : null}
      </div>

      {error ? <div className="form-error">{error}</div> : null}
    </Modal>
  );
}

interface WebhooksModalProps {
  store: ShopifyStoreAdmin;
  onClose: () => void;
}

function WebhooksModal({ store, onClose }: WebhooksModalProps) {
  const { push } = useToasts();
  const queryClient = useQueryClient();
  const [topic, setTopic] = useState('orders/create');
  const [address, setAddress] = useState('');

  const webhooksQuery = useQuery({
    queryKey: ['shopify', 'webhooks', store.code],
    queryFn: () => shopifyApi.listWebhooks(store.code)
  });

  const registerMutation = useMutation({
    mutationFn: (payload: RegisterWebhookPayload) =>
      shopifyApi.registerWebhook(store.code, payload),
    onSuccess: () => {
      push({ kind: 'success', title: 'Webhook registered' });
      setTopic('orders/create');
      setAddress('');
      void queryClient.invalidateQueries({ queryKey: ['shopify', 'webhooks', store.code] });
    },
    onError: (err: unknown) => {
      push({
        kind: 'error',
        title: 'Register failed',
        message: err instanceof ApiError ? err.problem?.detail ?? err.message : 'Unknown error'
      });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => shopifyApi.deleteWebhook(store.code, id),
    onSuccess: () => {
      push({ kind: 'success', title: 'Webhook deleted' });
      void queryClient.invalidateQueries({ queryKey: ['shopify', 'webhooks', store.code] });
    },
    onError: (err: unknown) => {
      push({
        kind: 'error',
        title: 'Delete failed',
        message: err instanceof ApiError ? err.problem?.detail ?? err.message : 'Unknown error'
      });
    }
  });

  return (
    <Modal
      open={true}
      size="lg"
      title={`Webhooks — ${store.code} (${store.shopifyDomain})`}
      onClose={onClose}
      footer={
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="card mb-4">
        <div className="card-header">
          <h3 className="card-title">Register new webhook</h3>
        </div>
        <div className="card-body">
          <Field
            label="Topic"
            hint="e.g. orders/create, orders/paid, refunds/create, app/uninstalled"
          >
            <input
              className="form-input"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
            />
          </Field>
          <Field label="Address (HTTPS URL)" hint="Shopify POSTs webhook payloads to this URL.">
            <input
              className="form-input"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="https://your-domain.com/api/v1/shopify/webhooks"
            />
          </Field>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!topic || !address || registerMutation.isPending}
            onClick={() => registerMutation.mutate({ topic, address })}
          >
            {registerMutation.isPending ? 'Registering...' : 'Register webhook'}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Registered webhooks</h3>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => webhooksQuery.refetch()}
            disabled={webhooksQuery.isFetching}
          >
            {webhooksQuery.isFetching ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        <div className="card-body no-pad">
          {webhooksQuery.isLoading ? (
            <LoadingState />
          ) : webhooksQuery.isError ? (
            <ErrorState error={webhooksQuery.error} onRetry={() => webhooksQuery.refetch()} />
          ) : (webhooksQuery.data ?? []).length === 0 ? (
            <div className="state">
              <h4>No webhooks registered</h4>
              <p className="text-small">Use the form above to add one.</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Topic</th>
                  <th>Address</th>
                  <th>Format</th>
                  <th>Created</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(webhooksQuery.data ?? []).map((w) => (
                  <tr key={w.id} className="no-hover">
                    <td className="font-mono text-small">{w.id}</td>
                    <td>{w.topic}</td>
                    <td className="text-small">{w.address}</td>
                    <td>{w.format}</td>
                    <td className="text-small">{formatDateTime(w.created_at)}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        disabled={deleteMutation.isPending}
                        onClick={() => {
                          if (window.confirm(`Delete webhook ${w.topic} (#${w.id})?`)) {
                            deleteMutation.mutate(w.id);
                          }
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Modal>
  );
}

interface BulkSecretsModalProps {
  stores: ShopifyStoreAdmin[];
  onClose: () => void;
  onSaved: () => void;
}

function BulkSecretsModal({ stores, onClose, onSaved }: BulkSecretsModalProps) {
  const { push } = useToasts();
  const [entries, setEntries] = useState<Record<string, { webhook_secret: string; access_token: string }>>(
    () => Object.fromEntries(stores.map((s) => [s.code, { webhook_secret: '', access_token: '' }]))
  );
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      const payload: BulkStoreSecretEntry[] = Object.entries(entries)
        .filter(([, v]) => v.webhook_secret.trim() || v.access_token.trim())
        .map(([code, v]) => ({
          code,
          webhook_secret: v.webhook_secret.trim() || undefined,
          access_token: v.access_token.trim() || undefined
        }));
      if (payload.length === 0) {
        throw new Error('Enter at least one secret to save.');
      }
      return shopifyApi.bulkUpdate(payload);
    },
    onSuccess: (res) => {
      const updated = res.results.filter((r) => r.status === 'updated').length;
      push({
        kind: 'success',
        title: 'Bulk update complete',
        message: `${updated} store(s) updated`
      });
      onSaved();
    },
    onError: (err: unknown) => {
      setError(
        err instanceof ApiError
          ? err.problem?.detail ?? err.message
          : err instanceof Error
            ? err.message
            : 'Update failed'
      );
    }
  });

  const setField = (code: string, field: 'webhook_secret' | 'access_token', value: string) => {
    setEntries((prev) => ({
      ...prev,
      [code]: {
        webhook_secret: prev[code]?.webhook_secret ?? '',
        access_token: prev[code]?.access_token ?? '',
        [field]: value
      }
    }));
  };

  return (
    <Modal
      open={true}
      size="lg"
      title="Bulk set Shopify secrets"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={mutation.isPending}
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
          >
            {mutation.isPending ? 'Saving...' : 'Save all'}
          </button>
        </>
      }
    >
      <p className="text-small text-muted mb-3">
        Paste each store&apos;s <strong>webhook signing secret</strong> (from Shopify admin →
        Settings → Notifications → Webhooks → reveal) and optionally a <strong>custom app access
        token</strong>. Leave a field blank to keep the existing value.
      </p>
      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>Store</th>
              <th>Shopify domain</th>
              <th>Current secret</th>
              <th>New webhook secret</th>
              <th>New access token (optional)</th>
            </tr>
          </thead>
          <tbody>
            {stores.map((store) => (
              <tr key={store.code} className="no-hover">
                <td>
                  <div className="font-mono">{store.code}</div>
                  <div className="text-small text-muted">{store.name}</div>
                </td>
                <td className="text-small">{store.shopifyDomain}</td>
                <td>
                  {store.hasWebhookSecret ? (
                    <Badge tone="success">Set</Badge>
                  ) : (
                    <Badge tone="warning">Missing</Badge>
                  )}
                </td>
                <td>
                  <input
                    type="password"
                    className="form-input"
                    value={entries[store.code]?.webhook_secret ?? ''}
                    autoComplete="new-password"
                    placeholder={store.hasWebhookSecret ? 'leave blank to keep' : 'paste secret'}
                    onChange={(e) => setField(store.code, 'webhook_secret', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    type="password"
                    className="form-input"
                    value={entries[store.code]?.access_token ?? ''}
                    autoComplete="new-password"
                    placeholder={store.hasAccessToken ? 'leave blank to keep' : 'shpat_...'}
                    onChange={(e) => setField(store.code, 'access_token', e.target.value)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {error ? <div className="form-error mt-3">{error}</div> : null}
    </Modal>
  );
}
